import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
// Old SchoolHeader deleted - use DashboardLayout with topnav instead
import SchoolSidebar from "../sidebar";
import CertificationServices from "../../../services/CertificationServices";
import "./index.css";

// Helper function to extract teacher records (same as in certifications/index.jsx)
const extractTeacherRecords = (payload) => {
  const list = (() => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.certifications)) return payload.certifications;
    if (Array.isArray(payload?.teachers)) return payload.teachers;
    if (Array.isArray(payload?.records)) return payload.records;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  })();

  return list.map((entry) => {
    const teacher =
      entry.teacher || entry.teacherInfo || entry.user || entry.profile || {};
    const certifications = Array.isArray(entry.records)
      ? entry.records
      : Array.isArray(entry.certifications)
      ? entry.certifications
      : Array.isArray(entry.courses)
      ? entry.courses
      : Array.isArray(entry.items)
      ? entry.items
      : [];
    const teacherId =
      teacher.id ||
      teacher._id ||
      entry.teacherId ||
      entry.userId ||
      entry.id ||
      "";
    const teacherName =
      teacher.name ||
      teacher.fullName ||
      `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() ||
      entry.teacherName ||
      "Unknown Teacher";
    return {
      teacherId,
      teacherName: teacherName || "Unknown Teacher",
      teacherEmail: teacher.email || entry.teacherEmail || "",
      certifications,
    };
  });
};

const statusLabels = {
  pending_review: "Pending Review",
  approved: "Approved",
  completed: "Completed",
  in_progress: "In Progress",
  not_started: "Not Started",
  expired: "Expired",
  rejected: "Rejected",
};

const statusOptions = [
  "pending_review",
  "in_progress",
  "completed",
  "approved",
  "not_started",
  "expired",
  "rejected",
];

const emptyFormState = {
  courseKey: "",
  status: "pending_review",
  dateCompleted: "",
  renewByDate: "",
  hours: "",
  notes: "",
};

const normalizeCourses = (payload) => {
  const list = (() => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.courses)) return payload.courses;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  })();

  return list
    .map((course) => {
      const key =
        course.courseKey || course.key || course.id || course._id || "";
      const label =
        course.title ||
        course.name ||
        course.courseTitle ||
        course.displayName ||
        key;
      return key ? { key, label } : null;
    })
    .filter(Boolean);
};

const mapTeacherEntry = (entry) => {
  if (!entry) {
    return null;
  }
  const teacher =
    entry.teacher || entry.teacherInfo || entry.user || entry.profile || {};
  const certifications = Array.isArray(entry.certifications)
    ? entry.certifications
    : [];
  const teacherId =
    teacher.id ||
    teacher._id ||
    entry.teacherId ||
    entry.userId ||
    entry.id ||
    "";
  if (!teacherId) {
    return null;
  }
  return {
    teacherId,
    teacherName:
      teacher.name ||
      teacher.fullName ||
      `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() ||
      entry.teacherName ||
      "Unknown Teacher",
    teacherEmail: teacher.email || entry.teacherEmail || "",
    certifications,
  };
};

const formatDateDisplay = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const getStatusBadgeClass = (status = "") => {
  const normalized = status.toLowerCase();
  switch (normalized) {
    case "completed":
    case "approved":
      return "badge bg-success-subtle text-success";
    case "pending_review":
      return "badge bg-warning-subtle text-warning";
    case "rejected":
    case "expired":
      return "badge bg-danger-subtle text-danger";
    case "in_progress":
      return "badge bg-info-subtle text-info";
    default:
      return "badge bg-light text-dark";
  }
};

const getCertificateIdentifier = (cert = {}, teacherId = "") =>
  cert._id ||
  cert.id ||
  cert.certificationId ||
  `${cert.courseKey || "record"}-${teacherId || cert.teacherId || "teacher"}`;

const ViewAction = () => {
  const [searchParams] = useSearchParams();
  const teacherId = searchParams.get("teacher_id");
  const teacherNameParam = searchParams.get("name") || "";

  const [isLoading, setIsLoading] = useState(true);
  const [teacherRecord, setTeacherRecord] = useState(null);
  const [courses, setCourses] = useState([]);
  const [formState, setFormState] = useState(emptyFormState);
  const [selectedCertification, setSelectedCertification] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  const loadTeacher = useCallback(async () => {
    if (!teacherId) {
      setTeacherRecord(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      // Use school endpoint and filter by teacherId (admin endpoint returns 403 for school admins)
      const response = await CertificationServices.getSchoolCertifications();
      const teachers = extractTeacherRecords(response);
      
      // Find the specific teacher
      const teacher = teachers.find(t => t.teacherId === teacherId);
      
      if (teacher) {
        setTeacherRecord({
          teacherId: teacher.teacherId,
          teacherName: teacherNameParam || teacher.teacherName,
          teacherEmail: teacher.teacherEmail,
          certifications: teacher.certifications || [],
        });
      } else {
        setError("Teacher not found in your school's certification records.");
        setTeacherRecord(null);
      }
    } catch (err) {
      console.error("Failed to load teacher certifications", err);
      setError(
        "We couldn't load certification records right now. Please try again."
      );
      setTeacherRecord(null);
    } finally {
      setIsLoading(false);
    }
  }, [teacherId, teacherNameParam]);

  const loadCourses = useCallback(async () => {
    try {
      const response = await CertificationServices.getCourses();
      setCourses(normalizeCourses(response));
    } catch (err) {
      console.error("Failed to load certification courses", err);
      setCourses([]);
    }
  }, []);

  useEffect(() => {
    loadTeacher();
  }, [loadTeacher]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  useEffect(() => {
    if (
      !selectedCertification &&
      courses.length > 0 &&
      !formState.courseKey
    ) {
      setFormState((prev) => ({ ...prev, courseKey: courses[0].key }));
    }
  }, [courses, selectedCertification, formState.courseKey]);

  const summaryCounts = useMemo(() => {
    const certifications = teacherRecord?.certifications || [];
    const total = certifications.length;
    const completed = certifications.filter((cert) =>
      ["completed", "approved"].includes((cert.status || "").toLowerCase())
    ).length;
    const pendingReview = certifications.filter(
      (cert) => (cert.status || "").toLowerCase() === "pending_review"
    ).length;
    return { total, completed, pendingReview };
  }, [teacherRecord]);

  const sortedCertifications = useMemo(() => {
    const certifications = teacherRecord?.certifications || [];
    return [...certifications].sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.dateCompleted || 0).getTime();
      const dateB = new Date(b.updatedAt || b.dateCompleted || 0).getTime();
      return dateB - dateA;
    });
  }, [teacherRecord]);

  const handleEdit = (certification) => {
    setSelectedCertification(certification);
    setFormState({
      courseKey: certification.courseKey || "",
      status: certification.status || "pending_review",
      dateCompleted: toDateInputValue(certification.dateCompleted),
      renewByDate: toDateInputValue(certification.renewByDate),
      hours:
        certification.hours !== undefined && certification.hours !== null
          ? String(certification.hours)
          : "",
      notes: certification.notes || "",
    });
  };

  const resetForm = () => {
    setSelectedCertification(null);
    setFormState((prev) => ({
      ...emptyFormState,
      courseKey: courses[0]?.key || "",
    }));
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDownloadCertificate = async (cert) => {
    if (!cert?.certificateUrl) {
      Swal.fire(
        "No certificate",
        "There is no certificate file available yet.",
        "info"
      );
      return;
    }

    const identifier = getCertificateIdentifier(cert, teacherId);
    setDownloadingId(identifier);
    try {
      const courseLabel =
        cert.courseTitle ||
        cert.courseName ||
        cert.course?.title ||
        cert.courseKey ||
        "Certificate";
      const safeCourseLabel = courseLabel.replace(/[\\/:*?"<>|]+/g, "_");
      const { blob, filename, contentType } =
        await CertificationServices.downloadCertificate({
          teacherId,
          certId:
            cert._id || cert.id || cert.certificationId || identifier,
          fallbackName: `${safeCourseLabel}.pdf`,
        });
      const blobUrl = window.URL.createObjectURL(
        new Blob([blob], { type: contentType })
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || `${safeCourseLabel}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Failed to download certificate", err);
      Swal.fire(
        "Download failed",
        "We couldn't download this certificate. Please try again.",
        "error"
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!teacherId) {
      Swal.fire(
        "Missing teacher",
        "We could not determine which teacher to update.",
        "error"
      );
      return;
    }
    if (!formState.courseKey) {
      Swal.fire(
        "Course required",
        "Please choose a certification course before saving.",
        "warning"
      );
      return;
    }
    const payload = {
      courseKey: formState.courseKey,
      status: formState.status || undefined,
      dateCompleted: formState.dateCompleted || undefined,
      renewByDate: formState.renewByDate || undefined,
      hours:
        formState.hours !== "" && !Number.isNaN(Number(formState.hours))
          ? Number(formState.hours)
          : undefined,
      notes: formState.notes || undefined,
    };

    if (selectedCertification) {
      payload.certificationId =
        selectedCertification._id ||
        selectedCertification.id ||
        selectedCertification.certificationId;
    }

    setIsSaving(true);
    try {
      await CertificationServices.assignCertificationToTeacher(
        teacherId,
        payload
      );
      await loadTeacher();
      resetForm();
      Swal.fire(
        "Saved",
        "The certification record has been updated.",
        "success"
      );
    } catch (err) {
      console.error("Failed to assign certification", err);
      Swal.fire(
        "Error",
        "We could not update the certification record. Please try again.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const teacherDisplayName =
    teacherRecord?.teacherName || teacherNameParam || "Teacher";

  return (
    <div className="main-wrapper">
      {/* Old SchoolHeader deleted */}
      <div className="breadcrumb-bar breadcrumb-bar-info">
        <div className="container">
          <div className="row">
            <div className="col-md-12 col-12">
              <div className="breadcrumb-list">
                <h2 className="breadcrumb-title">
                  {teacherDisplayName}&apos;s Certifications
                </h2>
                <nav aria-label="breadcrumb" className="page-breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/main-dashboard">Home</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Certifications
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="page-content">
        <div className="container">
          <div className="row">
            <SchoolSidebar />
            <div className="col-xl-9 col-lg-9">
              {isLoading ? (
                <div className="card">
                  <div className="card-body text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                </div>
              ) : !teacherRecord ? (
                <div className="card">
                  <div className="card-body">
                    <p className="mb-3">
                      {error ||
                        "We couldn't find any certification data for this teacher yet."}
                    </p>
                    <Link to="/school-certifications" className="btn btn-primary">
                      Back to certifications overview
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div className="card mb-4">
                    <div className="card-body">
                      <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
                        <div>
                          <h4 className="mb-1">{teacherDisplayName}</h4>
                          {teacherRecord.teacherEmail ? (
                            <p className="mb-0 text-muted">
                              {teacherRecord.teacherEmail}
                            </p>
                          ) : (
                            <p className="mb-0 text-muted">
                              No email on file for this teacher.
                            </p>
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                          <span className="badge bg-primary-subtle text-primary fw-semibold">
                            Total: {summaryCounts.total}
                          </span>
                          <span className="badge bg-success-subtle text-success fw-semibold">
                            Completed: {summaryCounts.completed}
                          </span>
                          <span className="badge bg-warning-subtle text-warning fw-semibold">
                            Pending review: {summaryCounts.pendingReview}
                          </span>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={loadTeacher}
                          >
                            Refresh
                          </button>
                        </div>
                      </div>
                      <div className="table-responsive mt-4">
                        <table className="table table-striped align-middle">
                          <thead>
                            <tr>
                              <th>Course</th>
                              <th>Status</th>
                              <th>Completed</th>
                              <th>Renew by</th>
                              <th>Hours</th>
                              <th>Certificate</th>
                              <th>Last update</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedCertifications.length === 0 ? (
                              <tr>
                                <td colSpan="8" className="text-muted text-center">
                                  No certification records saved yet.
                                </td>
                              </tr>
                            ) : (
                              sortedCertifications.map((cert) => {
                                const certId = getCertificateIdentifier(
                                  cert,
                                  teacherId
                                );
                                const statusKey = cert.status || "not_started";
                                const badgeLabel =
                                  statusLabels[statusKey] ||
                                  statusKey
                                    .split(/[_\s-]+/)
                                    .map(
                                      (part) =>
                                        part.charAt(0).toUpperCase() +
                                        part.slice(1)
                                    )
                                    .join(" ");
                                const badgeClass = getStatusBadgeClass(statusKey);
                                const courseLabel =
                                  cert.courseTitle ||
                                  cert.courseName ||
                                  cert.course?.title ||
                                  cert.courseKey ||
                                  "Certification Course";
                                const rowKey =
                                  cert._id ||
                                  cert.id ||
                                  `${cert.courseKey}-${cert.updatedAt}`;
                                return (
                                  <tr key={rowKey}>
                                    <td>{courseLabel}</td>
                                    <td>
                                      <span className={badgeClass}>
                                        {badgeLabel}
                                      </span>
                                    </td>
                                    <td>{formatDateDisplay(cert.dateCompleted)}</td>
                                    <td>{formatDateDisplay(cert.renewByDate)}</td>
                                    <td>
                                      {cert.hours !== undefined &&
                                      cert.hours !== null
                                        ? cert.hours
                                        : "—"}
                                    </td>
                                    <td>
                                      {cert.certificateUrl ? (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary"
                                          onClick={() =>
                                            handleDownloadCertificate(cert)
                                          }
                                          disabled={downloadingId === certId}
                                        >
                                          {downloadingId === certId
                                            ? "Downloading..."
                                            : "Download"}
                                        </button>
                                      ) : (
                                        <span className="text-muted">No file</span>
                                      )}
                                    </td>
                                    <td>
                                      {formatDateDisplay(
                                        cert.updatedAt || cert.createdAt
                                      )}
                                    </td>
                                    <td className="text-end">
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handleEdit(cert)}
                                      >
                                        Edit
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
                        <div>
                          <h5 className="mb-1">
                            {selectedCertification
                              ? "Update certification record"
                              : "Assign a certification"}
                          </h5>
                          <p className="text-muted mb-0">
                            Submit course progress or adjustments on behalf of{" "}
                            {teacherDisplayName}. Changes are saved immediately.
                          </p>
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={resetForm}
                            disabled={isSaving}
                          >
                            {selectedCertification ? "Cancel edit" : "Clear form"}
                          </button>
                        </div>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label">Certification</label>
                            <select
                              className="form-control"
                              name="courseKey"
                              value={formState.courseKey}
                              onChange={handleFormChange}
                              required
                            >
                              <option value="">Select a course</option>
                              {courses.map((course) => (
                                <option key={course.key} value={course.key}>
                                  {course.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Status</label>
                            <select
                              className="form-control"
                              name="status"
                              value={formState.status}
                              onChange={handleFormChange}
                            >
                              {statusOptions.map((value) => (
                                <option key={value} value={value}>
                                  {statusLabels[value] ||
                                    value.charAt(0).toUpperCase() + value.slice(1)}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Date completed</label>
                            <input
                              type="date"
                              className="form-control"
                              name="dateCompleted"
                              value={formState.dateCompleted}
                              onChange={handleFormChange}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Renew by</label>
                            <input
                              type="date"
                              className="form-control"
                              name="renewByDate"
                              value={formState.renewByDate}
                              onChange={handleFormChange}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Hours</label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              className="form-control"
                              name="hours"
                              value={formState.hours}
                              onChange={handleFormChange}
                              placeholder="e.g. 1.5"
                            />
                          </div>
                          <div className="col-12">
                            <label className="form-label">Notes</label>
                            <textarea
                              className="form-control"
                              rows="3"
                              name="notes"
                              value={formState.notes}
                              onChange={handleFormChange}
                              placeholder="Optional notes for your records"
                            />
                          </div>
                        </div>
                        <div className="d-flex gap-2 mt-4">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSaving}
                          >
                            {isSaving
                              ? "Saving..."
                              : selectedCertification
                              ? "Update certification"
                              : "Assign certification"}
                          </button>
                          <Link
                            to="/school-certifications"
                            className="btn btn-link text-decoration-none"
                          >
                            Back to overview
                          </Link>
                        </div>
                      </form>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewAction;
